import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { TokenStorageService } from 'src/app/shared/services/token-storage.service';
import { SccRegisterDto, SccService } from '../../shared/services/scc.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { MatStepper } from '@angular/material/stepper';
import { AppStateService } from '../../../shared/services/app-state.service';
import { ClinicInterface } from '../../../shared/models/clinic.interface';
import { environment } from '../../../../environments/environment';

export enum ClinicEditModeEnum {
  Selection,
  Creation
}

@Component({
  selector: 'app-scc-register',
  templateUrl: './scc-register.component.html',
  styleUrls: ['./scc-register.component.scss']
})
export class SccRegisterComponent implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper;

  userFormGroup = this.formBuilder.group({});
  clinicFormGroup = this.formBuilder.group({});

  orderNumber = '';
  firstname = '';
  lastname = '';
  mail = '';

  clinic?: ClinicInterface;
  errorMessage?: string;
  isSending = false;
  usePassword = false;

  ClinicEditModeEnum = ClinicEditModeEnum;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sccService: SccService,
    private readonly tokenStorage: TokenStorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly appStateService: AppStateService
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe(params => {
      const orderNumber = params.get('o');
      const firstname = params.get('f');
      const lastname = params.get('l');
      const mail = params.get('m');

      this.usePassword = params.has('usePassword');

      if (!orderNumber && !this.usePassword) {
        void this.router.navigate(['/']);
        return;
      }

      this.orderNumber = orderNumber ?? '';
      if (firstname) {
        this.firstname = firstname;
      }

      if (lastname) {
        this.lastname = lastname;
      }

      if (mail) {
        this.mail = mail;
      }

      if (environment.production) {
        void this.router.navigate([], {
          queryParams: { o: null, f: null, l: null, m: null, usePassword: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }

  save() {
    if (!this.userFormGroup.valid || !this.clinicFormGroup.valid) {
      return;
    }

    this.isSending = true;
    this.errorMessage = undefined;

    this.snackbarService.openSnackBar('Inscription en cours');

    const form: SccRegisterDto = {
      user: {
        ordinalNumber: this.userFormGroup.get('orderNum')?.value ?? '',
        email: this.userFormGroup.get('email')?.value ?? '',
        firstName: this.userFormGroup.get('firstName')?.value ?? '',
        lastName: this.userFormGroup.get('lastName')?.value ?? '',
        password: this.userFormGroup.get('password')?.value ?? '',
      },
    };

    if (this.clinic?.id) {
      form.clinicId = this.clinic.id;
    } else {
      form.clinic = {
        name: this.clinicFormGroup.get('name')?.value ?? '',
        alias: 'ABC',
        address1: this.clinicFormGroup.get('address1')?.value ?? '',
        address2: this.clinicFormGroup.get('address2')?.value ?? '',
        postalCode: this.clinicFormGroup.get('zipCode')?.value ?? '',
        city: this.clinicFormGroup.get('city')?.value ?? '',
        email: this.clinicFormGroup.get('email')?.value ?? '',
        phone: this.clinicFormGroup.get('phone')?.value ?? '',
        clinicTypeId: this.clinicFormGroup.get('clinicType')?.value ?? 0
      };
    }

    this.sccService.register(form).subscribe({
      next: result => {
        this.isSending = false;

        if (!result?.token) {
          this.errorMessage = 'Erreur technique. Merci de réessayer plus tard.';
          console.error('Pas de token ???');
          return;
        }

        // Définir le clinicId AVANT de sauvegarder le token pour éviter qu'il soit écrasé par le navigation component
        this.appStateService.clinicId = result.clinic.id;
        this.tokenStorage.saveTokenAndUserInfo(result.token);

        if (result.needPaymentMethod && !this.clinic?.hasPaymentMethod) {
          this.stepper.next();
        } else {
          void this.router.navigate(['/'], {
            replaceUrl: true
          });
        }
      },
      error: error => {
        this.isSending = false;
        this.errorMessage = error?.message ?? 'Erreur technique. Merci de réessayer plus tard.';
      }
    });
  }

  setClinic(clinic?: ClinicInterface) {
    this.clinic = clinic;
  }
}
